import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Star, ShieldCheck, Clock, CreditCard } from 'lucide-react';
type OfferId = 'kit' | 'megapack';
interface Offer {
  id: OfferId;
  name: string;
  desc: string;
  oldPrice: string;
  price: string;
  url: string;
  recommended?: boolean;
  save?: string;
}
const OFFERS: Offer[] = [{
  id: 'kit',
  name: 'KIT ESENCIAL',
  desc: '6 tomos + 3 bonos',
  oldPrice: '$35.000',
  price: '$19.990',
  url: 'https://checkout.solucionesya.shop/cart/49392226468088:1'
}, {
  id: 'megapack',
  name: 'MEGAPACK COMPLETO',
  desc: '8 tomos + 3 bonos + Finanzas',
  oldPrice: '$45.000',
  price: '$27.990',
  url: 'https://checkout.solucionesya.shop/cart/49392248127736:1',
  recommended: true,
  save: 'AHORRÁS $17.010'
}];
interface OfferSelectorProps {
  theme?: 'light' | 'dark';
}
export function OfferSelector({
  theme = 'light'
}: OfferSelectorProps) {
  // Higher-priced option preselected
  const [selected, setSelected] = useState<OfferId>('megapack');
  const selectedOffer = OFFERS.find(o => o.id === selected)!;
  const isDark = theme === 'dark';
  return <div className="max-w-3xl mx-auto" data-id="element-164">
      <div role="radiogroup" aria-label="Elegí tu sistema de gestión" className="flex flex-col gap-3 md:gap-4" data-id="element-165">
        {OFFERS.map(offer => {
        const isSelected = selected === offer.id;
        return <button key={offer.id} type="button" role="radio" aria-checked={isSelected} onClick={() => setSelected(offer.id)} className={`relative w-full text-left rounded-2xl border-2 p-4 md:p-5 transition-all flex items-center gap-3 md:gap-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 ${isSelected ? 'border-amber-400 bg-amber-50 shadow-lg ring-2 ring-amber-400/40' : isDark ? 'border-slate-700 bg-slate-800/60 hover:border-slate-500' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-166">
              {/* Selection indicator */}
              <span className={`flex-shrink-0 h-5 w-5 md:h-6 md:w-6 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'border-amber-500 bg-amber-500' : isDark ? 'border-slate-500' : 'border-slate-300'}`} data-id="element-167">
                {isSelected && <Check className="h-3 w-3 md:h-4 md:w-4 text-white" data-id="element-168" />}
              </span>

              {/* Name + description */}
              <div className="flex-grow min-w-0" data-id="element-169">
                <div className="flex flex-wrap items-center gap-2" data-id="element-170">
                  <h3 className={`font-bold text-sm md:text-lg leading-tight ${isSelected ? 'text-slate-900' : isDark ? 'text-white' : 'text-slate-900'}`} data-id="element-171">
                    {offer.name}
                  </h3>
                  {offer.recommended && <span className="bg-amber-400 text-slate-900 font-bold px-2 py-0.5 rounded-full text-[10px] md:text-xs flex items-center gap-1 whitespace-nowrap" data-id="element-172">
                      <Star className="h-3 w-3 fill-slate-900" data-id="element-173" /> MÁS ELEGIDO
                    </span>}
                </div>
                <p className={`text-xs md:text-sm font-medium ${isSelected ? 'text-amber-700' : isDark ? 'text-slate-400' : 'text-slate-500'}`} data-id="element-174">
                  {offer.desc}
                </p>
              </div>

              {/* Price */}
              <div className="flex-shrink-0 text-right" data-id="element-175">
                <span className={`line-through text-[10px] md:text-sm block leading-none mb-0.5 ${isSelected ? 'text-slate-400' : isDark ? 'text-slate-500' : 'text-slate-400'}`} data-id="element-176">
                  {offer.oldPrice}
                </span>
                <div className="flex items-baseline justify-end gap-1" data-id="element-177">
                  <span className={`text-xl md:text-3xl font-extrabold ${isSelected ? 'text-slate-900' : isDark ? 'text-white' : 'text-slate-900'}`} data-id="element-178">
                    {offer.price}
                  </span>
                  <span className={`text-[10px] md:text-sm ${isSelected ? 'text-slate-500' : isDark ? 'text-slate-400' : 'text-slate-500'}`} data-id="element-179">
                    ARS
                  </span>
                </div>
                {offer.save && <p className="text-teal-600 text-[10px] md:text-xs font-bold mt-0.5" data-id="element-180">
                    {offer.save}
                  </p>}
              </div>
            </button>;
      })}
      </div>

      {/* Shared purchase button */}
      <motion.div whileHover={{
      scale: 1.02
    }} whileTap={{
      scale: 0.98
    }} className="mt-5 md:mt-6" data-id="element-181">
        <a href={selectedOffer.url} className="flex w-full items-center justify-center bg-amber-500 text-slate-900 hover:bg-amber-400 font-bold rounded-xl px-6 py-4 text-base md:text-xl shadow-lg shadow-amber-500/30 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2" data-id="element-182">
          {selected === 'megapack' ? 'SÍ, QUIERO EL MEGAPACK COMPLETO' : 'COMPRAR KIT ESENCIAL'}
        </a>
      </motion.div>

      <div className={`flex flex-wrap justify-center gap-3 md:gap-5 mt-4 md:mt-5 text-[10px] md:text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`} data-id="element-183">
        <span className="flex items-center gap-1" data-id="element-184">
          <CreditCard className="h-3 w-3 md:h-4 md:w-4" data-id="element-185" /> Pago seguro
        </span>
        <span className="flex items-center gap-1" data-id="element-186">
          <Clock className="h-3 w-3 md:h-4 md:w-4" data-id="element-187" /> Acceso en 5 min
        </span>
        <span className="flex items-center gap-1" data-id="element-188">
          <ShieldCheck className="h-3 w-3 md:h-4 md:w-4" data-id="element-189" /> Garantía 7 días
        </span>
      </div>
    </div>;
}